import type { ChatProfile } from '@customTypes/chat';
import type { ArtistItem } from '@customTypes/types';
import {
  Alert,
  AppShell,
  Button,
  Card,
  Center,
  Flex,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Title,
  Progress,
  Badge,
  Select
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useDeleteArtistMutation } from '@/features/admin-panel/api/deleteArtist/hook';
import { useGetArtistsActivityListQuery } from '@/features/admin-panel/api/getArtistsActivitiList/hook';
import {
  useCreateConversationMutation,
  useGetChatProfileQuery
} from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { useShowFullArtistProfileQuery } from '@/features/work/api/show-full-artist-profile';
import { ArtistFullProfile } from '@/features/work/view/artist-full-profile/ArtistFullProfile';
import { useArtistProfileProgressQuery } from '@/shared/api/requests/profile-progress/artist';

type AdminArtistRowProps = {
  artist: ArtistItem;
  descendingIndex: number;
  chatProfile: ChatProfile | null;
  handleConversation: (senderId: string, recipientId: string) => void;
  setCurrentProfileId: (id: string) => void;
  openProfile: () => void;
  onDeleteClick: (id: string) => void;
};

// Child row component for per-artist hooks
const AdminArtistRow = ({
  artist,
  descendingIndex,
  chatProfile,
  handleConversation,
  setCurrentProfileId,
  openProfile,
  onDeleteClick
}: AdminArtistRowProps) => {
  const { data: fullProfile, isLoading: isStatusLoading } =
    useShowFullArtistProfileQuery({
      profileId: artist.artistProfileId.toString()
    });
  const { data: progressData, isLoading: isProgressLoading } =
    useArtistProfileProgressQuery({
      profileId: artist.artistProfileId.toString()
    });
  const status = fullProfile?.data?.status;
  const progress = progressData?.data?.totalCompletion;

  const { profile, currentSessionId } = useProfileStore();
  const { updateChatProfile } = useChatStore();

  const chatProfileQuery = useGetChatProfileQuery({
    profileType: profile?.role!,
    profileId: currentSessionId?.toString()!
  });

  useEffect(() => {
    if (chatProfileQuery.data?.data.chatProfileId) {
      updateChatProfile(chatProfileQuery.data?.data);
    }
  }, [chatProfileQuery.data?.data?.chatProfileId]);

  const ARTIST_STATUS_COLORS: Record<string, string> = {
    LookingForWork: 'green',
    OnContract: 'blue',
    FinishingContract: 'yellow',
    OnVacation: 'orange',
    HiddenFromSearch: 'gray'
  };

  const ARTIST_STATUS_LABELS: Record<string, string> = {
    LookingForWork: 'Поиск', // or 'Looking' for EN
    OnContract: 'Контракт', // or 'Contract'
    FinishingContract: 'Финиш', // or 'Finish'
    OnVacation: 'Отпуск', // or 'Vacation'
    HiddenFromSearch: 'Скрыт' // or 'Hidden'
  };

  return (
    <Table.Tr key={artist.artistProfileId}>
      <Table.Td>{descendingIndex}</Table.Td>
      <Table.Td>{artist.artistProfileId ?? '-'}</Table.Td>
      <Table.Td>{artist.firstName ?? '-'}</Table.Td>
      <Table.Td>{artist.lastName ?? '-'}</Table.Td>
      <Table.Td>{artist.email ?? '-'}</Table.Td>
      <Table.Td>{artist.phoneNumber ?? '-'}</Table.Td>
      <Table.Td>{artist.referrerEmail ?? '-'}</Table.Td>
      <Table.Td>{artist.loyaltyPoints ?? '-'}</Table.Td>
      <Table.Td>
        {isStatusLoading ? (
          <Loader size='xs' />
        ) : status ? (
          <Badge
            color={ARTIST_STATUS_COLORS[status] || 'gray'}
            variant='filled'
          >
            {ARTIST_STATUS_LABELS[status] || status}
          </Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        {isProgressLoading ? (
          <Loader size='xs' />
        ) : typeof progress === 'number' ? (
          <Flex direction='column' gap={4}>
            <Text size='xs'>{progress}%</Text>
            <Progress radius='xl' size='sm' value={progress} />
          </Flex>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Flex gap='xs' justify='center'>
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              setCurrentProfileId(artist.artistProfileId.toString());
              openProfile();
            }}
          >
            Профиль
          </Button>
          <Button
            disabled={!artist.chatProfileId}
            size='xs'
            variant='filled'
            onClick={() =>
              chatProfile &&
              handleConversation(
                artist.chatProfileId.toString(),
                chatProfile.chatProfileId.toString()
              )
            }
          >
            Чат
          </Button>
          <Button
            color='red'
            size='xs'
            variant='outline'
            onClick={() => onDeleteClick(artist.artistProfileId.toString())}
          >
            Удалить
          </Button>
        </Flex>
      </Table.Td>
    </Table.Tr>
  );
};

export const AdminArtistsList = () => {
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [debouncedName] = useDebouncedValue(searchName, 300);
  const [debouncedLastName] = useDebouncedValue(searchLastName, 300);
  const [debouncedPhone] = useDebouncedValue(searchPhone, 300);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useGetArtistsActivityListQuery({
    pageNumber: page.toString(),
    pageSize: '10',
    FirstName: debouncedName,
    LastName: debouncedLastName,
    PhoneNumber: debouncedPhone
  });

  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [debouncedName, debouncedLastName, debouncedPhone]);

  /* ──────────────────────────────────────────────
     ↓ NEW: данные для обратной нумерации ↓
  ────────────────────────────────────────────── */
  // const pageSize = data?.data.pageSize ?? 10;
  const pageSize = 10;

  const totalPages = data?.data.totalPages ?? 0;
  const totalCount = data?.data.totalCount ?? totalPages * pageSize;
  /* ────────────────────────────────────────────── */

  const { chatProfile } = useChatStore();
  const createConversation = useCreateConversationMutation();
  const deleteArtist = useDeleteArtistMutation();

  // Profile Modal
  const [isProfileOpen, { open: openProfile, close: closeProfile }] =
    useDisclosure();
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);

  // Delete Confirmation Modal
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure();
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');

  const handleConversation = async (senderId: string, recipientId: string) => {
    const response = await createConversation.mutateAsync({
      params: {
        senderProfileId: senderId,
        recipientProfileId: recipientId,
        isDirect: true
      }
    });
    const convId = response.data.conversationId.toString();
    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: convId }
    });
  };

  const onDeleteClick = (profileId: string) => {
    setDeleteProfileId(profileId);
    setConfirmInput('');
    openDelete();
  };

  const confirmDelete = async () => {
    if (!deleteProfileId) return;
    await deleteArtist.mutateAsync({ profileId: deleteProfileId });
    closeDelete();
    refetch();
  };

  const ARTIST_STATUS_OPTIONS = [
    { value: 'HiddenFromSearch', label: 'Скрыт' },
    { value: 'LookingForWork', label: 'Поиск' },
    { value: 'OnContract', label: 'Контракт' }
  ];

  // Filter the table data by selected status

  const filteredArtists =
    data?.data.items.filter((artist) => {
      if (!statusFilter) return true; // no filter selected
      return artist.profileStatus === statusFilter;
    }) ?? [];

  // Slice for current page
  const paginatedArtists = filteredArtists.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Total pages
  const totalPagesClient = Math.ceil(filteredArtists.length / pageSize);

  return (
    <AppShell.Main>
      {/* Artist Profile Modal */}
      <Modal
        withCloseButton
        opened={isProfileOpen}
        overlayProps={{ blur: 8, opacity: 0.6 }}
        onClose={closeProfile}
      >
        {currentProfileId && (
          <ScrollArea p='md'>
            <ArtistFullProfile artistProfileId={currentProfileId} />
          </ScrollArea>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        withCloseButton
        opened={isDeleteOpen}
        overlayProps={{ blur: 8, opacity: 0.6 }}
        title='Удаление артиста'
        onClose={closeDelete}
      >
        <Text mb='sm'>
          Вы уверены, что хотите удалить этого артиста? Это действие необратимо.
          Введите &quot;delete&quot; для подтверждения.
        </Text>
        <TextInput
          placeholder='Введите delete для подтверждения'
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.currentTarget.value)}
        />
        <Flex gap='xs' justify='end' mt='md'>
          <Button variant='default' onClick={closeDelete}>
            Отмена
          </Button>
          <Button
            color='red'
            disabled={confirmInput !== 'delete'}
            onClick={confirmDelete}
          >
            Удалить
          </Button>
        </Flex>
      </Modal>

      {/* Header */}
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Артисты</Title>
      </Flex>

      {/* Loading, Error, Empty States */}
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      )}
      {isError && (
        <Alert color='red' mb='md' title='Ошибка'>
          Не удалось загрузить список артистов.
        </Alert>
      )}
      {!isLoading && data?.data.items.length === 0 && (
        <Center>
          <Text c='dimmed'>Нет артистов для отображения.</Text>
        </Center>
      )}
      {/* Artists Table */}
      {!isLoading && data && (
        <Card withBorder p='lg' radius='md' shadow='sm'>
          <Flex gap='lg' mb='sm' wrap='wrap'>
            <TextInput
              placeholder='Поиск по имени'
              style={{ flex: '1 1 40%' }}
              value={searchName}
              onChange={(e) => setSearchName(e.currentTarget.value)}
            />
            <TextInput
              placeholder='Поиск по фамилии'
              style={{ flex: '1 1 40%' }}
              value={searchLastName}
              onChange={(e) => setSearchLastName(e.currentTarget.value)}
            />
            <TextInput
              placeholder='Поиск по телефону'
              style={{ flex: '1 1 40%' }}
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.currentTarget.value)}
            />
            <Select
              clearable
              data={ARTIST_STATUS_OPTIONS}
              placeholder='Фильтр по статусу'
              style={{ flex: '1 1 40%' }}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Flex>

          <br />
          <ScrollArea>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>No</Table.Th>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Имя</Table.Th>
                  <Table.Th>Фамилия</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th>Кто пригласил</Table.Th>
                  <Table.Th>Баллы</Table.Th>
                  <Table.Th>Статус профиля</Table.Th>
                  <Table.Th>Прогресс профиля</Table.Th>
                  <Table.Th />
                  {/* Actions */}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {/* {data.data.items.map((artist, index) => { */}
                {paginatedArtists.map((artist, index) => {
                  const descendingIndex =
                    totalCount - ((page - 1) * pageSize + index);
                  return (
                    <AdminArtistRow
                      key={artist.artistProfileId}
                      artist={artist}
                      chatProfile={chatProfile}
                      descendingIndex={descendingIndex}
                      handleConversation={handleConversation}
                      openProfile={openProfile}
                      setCurrentProfileId={setCurrentProfileId}
                      onDeleteClick={onDeleteClick}
                    />
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Flex justify='center' mt='md'>
            <Pagination
              boundaries={1}
              siblings={1}
              total={totalPagesClient}
              // total={data.data.totalPages}
              value={page}
              onChange={setPage}
            />
          </Flex>
        </Card>
      )}
    </AppShell.Main>
  );
};
